/**
 * Customer buying journey: one place to track stages/events to Clarity and Firestore.
 * Allowed only on digipalz.io and www.digipalz.io.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db, auth } from '@/firebase/config'
import { GTM_ALLOWED_HOSTNAMES } from '@/config/gtm'
import { isYouthEnvironment } from '@/config/youthEnvironment'
import { setClarityTags, getClaritySessionUrl } from '@/services/clarityService'
import gtmService from '@/services/gtm/gtmService'
import { sendBuyingJourneyToMeta } from '@/services/metaPixelService'
import { getOrCreateAnonId, getPersistedUtm } from '@/utils/trackingSpine'
import {
  BUYING_JOURNEY_STAGES,
  BUYING_JOURNEY_EVENTS,
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS,
  JOURNEY_USER_TYPES,
  ALLOW_LOCALHOST_FOR_JOURNEY_TESTING
} from '@/constants/buyingJourney'

const JOURNEY_COLLECTION = 'customerJourneyEvents'
const JOURNEY_USER_TYPE_KEY = 'digipalz_journey_user_type'
const JOURNEY_PLAN_KEY = 'digipalz_journey_plan'
const JOURNEY_SENT_KEY_PREFIX = 'digipalz_journey_sent_'

function getStoredJourneyUserType() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    return window.localStorage.getItem(JOURNEY_USER_TYPE_KEY) || null
  } catch (e) {
    return null
  }
}

export function setJourneyUserType(userType) {
  if (!userType || typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(JOURNEY_USER_TYPE_KEY, String(userType))
  } catch (e) {
    // ignore
  }
}

function getStoredJourneyPlan() {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    return window.localStorage.getItem(JOURNEY_PLAN_KEY) || null
  } catch (e) {
    return null
  }
}

export function setJourneyPlanType(planType) {
  if (!planType || typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(JOURNEY_PLAN_KEY, String(planType))
  } catch (e) {
    // ignore
  }
}

function isProductionDomain() {
  if (typeof window === 'undefined' || !window.location?.hostname) return false
  return GTM_ALLOWED_HOSTNAMES.includes(window.location.hostname)
}

function isLocalhost() {
  if (typeof window === 'undefined' || !window.location?.hostname) return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1'
}

function getJourneyTrackingReason() {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : 'unknown'
  if (isProductionDomain())
    return { allowed: true, reason: 'production', hostname }
  if (ALLOW_LOCALHOST_FOR_JOURNEY_TESTING && isLocalhost()) {
    return { allowed: true, reason: 'localhost_testing', hostname }
  }
  return { allowed: false, reason: 'domain_not_allowed', hostname }
}

function isJourneyTrackingAllowed() {
  return getJourneyTrackingReason().allowed
}

function getDeviceType() {
  if (typeof window === 'undefined' || !window.navigator?.userAgent) return null
  const ua = window.navigator.userAgent.toLowerCase()
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet'
  if (
    /mobile|iphone|ipod|android.*mobile|blackberry|opera mini|iemobile/i.test(
      ua
    )
  )
    return 'mobile'
  return 'desktop'
}

function getPlanBilling(planType) {
  if (!planType || typeof planType !== 'string') return null
  const p = planType.toLowerCase()
  if (p.includes('yearly') || p.includes('annual')) return 'yearly'
  if (p.includes('monthly')) return 'monthly'
  return null
}

/**
 * Track a buying journey stage/event. Sends to Clarity (custom tags) and saves to Firestore.
 * No-op when domain is not allowed (digipalz.io, www.digipalz.io only).
 * @param {object} params
 * @param {string} params.stage - One of BUYING_JOURNEY_STAGES (plan_view, plan_select, checkout_start, checkout_complete, checkout_cancel)
 * @param {string} params.eventName - One of BUYING_JOURNEY_EVENTS (plan_page_view, plan_selected, checkout_redirect, payment_success, payment_cancel)
 * @param {string} [params.userType] - parent | school | educator
 * @param {string} [params.userId] - Firebase UID when logged in
 * @param {string} [params.planType] - Full plan id: singleMonthly, singleYearly, multipleMonthly, multipleYearly (plan is derived as monthly | yearly)
 * @param {object} [params.payload] - Extra context (e.g. seats, amount) - keep small for DB
 */
export async function trackBuyingJourney({
  stage,
  eventName,
  userType,
  userId,
  planType,
  subscriptionStatus,
  selectionContext,
  dedupeKey: payloadDedupeKey,
  ...payload
}) {
  const trackingReason = getJourneyTrackingReason()
  if (!trackingReason.allowed) return

  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const anonymousId = getOrCreateAnonId()
  const effectiveUserType = userType || getStoredJourneyUserType()
  const effectivePlanType = planType ?? getStoredJourneyPlan()
  const effectiveUserId =
    userId || (auth?.currentUser?.uid ? String(auth.currentUser.uid) : null)

  const dedupeKey = payloadDedupeKey ?? `${stage ?? ''}_${eventName ?? ''}`
  const storageKey =
    JOURNEY_SENT_KEY_PREFIX +
    (effectiveUserId || anonymousId || 'anon') +
    '_' +
    dedupeKey
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(storageKey))
      return
    if (typeof localStorage !== 'undefined')
      localStorage.setItem(storageKey, '1')
  } catch (_) {}

  const utm = getPersistedUtm() || {}
  const deviceType = getDeviceType()
  const planBilling = getPlanBilling(effectivePlanType)

  const eventProperties = {
    anon_id: anonymousId || null,
    anonymousId: anonymousId || null,
    user_id: effectiveUserId || null,
    account_id: effectiveUserId || null,
    role: effectiveUserType || null,
    device_type: deviceType || null,
    page_path: path || null,
    utm_source: utm.utm_source || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    plan: planBilling || null,
    step: payload.step ?? null,
    subscription_status: subscriptionStatus ?? null,
    selection_context: selectionContext ?? null
  }

  // Child and student routes: first-party Firestore record only.
  // Nothing goes to Clarity, GTM, or Meta, and no child identifier is stored.
  const youthEnvironment = isYouthEnvironment()
  let lastSessionDebugLink = null

  if (!youthEnvironment) {
    const clarityTags = {
      journey_stage: stage || '',
      journey_event: eventName || '',
      journey_plan: effectivePlanType || '',
      journey_anon_id: anonymousId || '',
      journey_user_id: effectiveUserId || '',
      journey_device_type: deviceType || '',
      journey_path: path || ''
    }
    if (effectiveUserType) clarityTags.journey_user_type = effectiveUserType
    setClarityTags(clarityTags)

    const buyingJourneyPayload = {
      journey_stage: stage || '',
      journey_event: eventName || '',
      journey_plan: effectivePlanType || '',
      journey_user_type: effectiveUserType || null,
      ...eventProperties
    }
    gtmService.pushEvent('buying_journey', buyingJourneyPayload)
    sendBuyingJourneyToMeta(buyingJourneyPayload)

    try {
      lastSessionDebugLink = await getClaritySessionUrl()
    } catch (_) {
      lastSessionDebugLink = null
    }
  } else {
    delete payload.childId
  }

  const docPayload = {
    stage: stage || null,
    eventName: eventName || null,
    userType: effectiveUserType || null,
    userId: effectiveUserId || null,
    planType: effectivePlanType || null,
    path: path || '',
    anonymousId: anonymousId || null,
    anon_id: eventProperties.anon_id,
    user_id: eventProperties.user_id,
    account_id: eventProperties.account_id,
    role: eventProperties.role,
    device_type: eventProperties.device_type,
    page_path: eventProperties.page_path,
    utm_source: eventProperties.utm_source,
    utm_medium: eventProperties.utm_medium,
    utm_campaign: eventProperties.utm_campaign,
    plan: eventProperties.plan,
    step: eventProperties.step,
    subscription_status: eventProperties.subscription_status,
    selection_context: eventProperties.selection_context,
    last_session_debug_link: lastSessionDebugLink || null,
    ...payload
  }

  const sanitized = Object.fromEntries(
    Object.entries(docPayload).filter(([_, v]) => v !== undefined)
  )

  try {
    await addDoc(collection(db, JOURNEY_COLLECTION), {
      ...sanitized,
      timestamp: serverTimestamp(),
      clientTime: new Date().toISOString()
    })
  } catch (_) {}
}

export {
  BUYING_JOURNEY_STAGES,
  BUYING_JOURNEY_EVENTS,
  JOURNEY_FUNNEL_STAGES,
  JOURNEY_FUNNEL_EVENTS,
  JOURNEY_USER_TYPES,
  getStoredJourneyUserType,
  getStoredJourneyPlan
}

export default {
  trackBuyingJourney,
  setJourneyUserType,
  getStoredJourneyUserType,
  setJourneyPlanType,
  getStoredJourneyPlan,
  BUYING_JOURNEY_STAGES,
  BUYING_JOURNEY_EVENTS,
  JOURNEY_USER_TYPES
}
